// =============================================================================
// BP-07-03: useResource Hook
// =============================================================================

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getResource, rateResource } from '@/features/resources/api/resourcesApi';
import type { Resource, RateResourceRequest } from '@/features/resources/types/resourcesTypes';
import { resourceQueryKeys } from '@/features/resources/types/resourcesTypes';

interface UseResourceOptions {
  slug: string;
  enabled?: boolean;
}

/**
 * Hook for fetching a single resource by slug
 */
export function useResource(options: UseResourceOptions) {
  const { slug, enabled = true } = options;

  return useQuery({
    queryKey: resourceQueryKeys.detail(slug),
    queryFn: () => getResource(slug),
    enabled: enabled && !!slug,
    staleTime: 10 * 60 * 1000, // 10 minutes - content doesn't change often
  });
}

/**
 * Hook for rating a resource
 */
export function useRateResource() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ resourceId, rating }: { resourceId: string; rating: number }) =>
      rateResource(resourceId, { rating }),
    onSuccess: (data, variables) => {
      // Update the resource in cache with new rating
      queryClient.setQueriesData(
        { queryKey: resourceQueryKeys.details() },
        (oldData: Resource | undefined) => {
          if (oldData && oldData.id === variables.resourceId) {
            return {
              ...oldData,
              avgRating: data.avgRating,
              ratingCount: data.ratingCount,
              userRating: variables.rating,
            };
          }
          return oldData;
        }
      );
      
      // Invalidate lists to reflect new rating
      queryClient.invalidateQueries({ queryKey: resourceQueryKeys.lists() });
    },
  });
}

/**
 * Hook to prefetch a resource (for hover states)
 */
export function usePrefetchResource() {
  const queryClient = useQueryClient();

  return (slug: string) => {
    queryClient.prefetchQuery({
      queryKey: resourceQueryKeys.detail(slug),
      queryFn: () => getResource(slug),
      staleTime: 10 * 60 * 1000,
    });
  };
}

export default useResource;

/**
 * Featured Resources Hook
 */
export function useFeaturedResources() {
  return {
    data: [],
    isLoading: false,
    error: null,
  };
}

